import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';

export type CatchCatalogCategory = 'modality' | 'location' | 'bait' | 'lure';

export interface CatchCatalogRow {
    id: string;
    category: CatchCatalogCategory;
    slug: string;
    name: string;
    water_type: 'dulce' | 'salada' | 'both';
    icon: string | null;
    visual_kind: string | null;
    material_kind: 'cebo' | 'señuelo' | null;
    display_order: number;
    is_active: boolean;
    is_default: boolean;
    updated_at?: string;
}

export interface CatchCatalogUpsertInput {
    id?: string | null;
    category: CatchCatalogCategory;
    slug?: string;
    name: string;
    water_type: 'dulce' | 'salada' | 'both';
    icon?: string;
    visual_kind?: string;
    material_kind?: 'cebo' | 'señuelo' | '';
    display_order?: number | null;
    is_active?: boolean;
    is_default?: boolean;
}

const QK = ['admin', 'catch-form-catalog'] as const;

export function useCatchFormCatalog(category: CatchCatalogCategory, includeInactive = true) {
    return useQuery({
        queryKey: [...QK, category, includeInactive],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_list_catch_form_catalog', {
                p_category: category,
                p_include_inactive: includeInactive,
            });
            if (error) throw new Error(error.message);
            return normalizeRpcResponse(data) as CatchCatalogRow[];
        },
        staleTime: 10_000,
    });
}

export function useUpsertCatchFormCatalog() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (input: CatchCatalogUpsertInput) => {
            const { data, error } = await dbClient.rpc('admin_upsert_catch_form_catalog', {
                p_id: input.id ?? null,
                p_category: input.category,
                p_slug: input.slug?.trim() || null,
                p_name: input.name.trim(),
                p_water_type: input.water_type,
                p_icon: input.icon ?? null,
                p_visual_kind: input.visual_kind ?? null,
                p_material_kind:
                    input.category === 'modality' ? (input.material_kind || null) : null,
                p_display_order: input.display_order ?? null,
                p_is_active: input.is_active ?? true,
                p_is_default: input.is_default ?? false,
            });
            if (error) throw new Error(error.message);
            return data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK });
        },
    });
}

export function useSetCatchFormCatalogActive() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
            const { error } = await dbClient.rpc('admin_set_catch_form_catalog_active', {
                p_id: id,
                p_is_active: isActive,
            });
            if (error) throw new Error(error.message);
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: QK });
        },
    });
}
